import { GraphicsBounds } from "./GraphicsBounds";
import { BaseRender2DType, RepaintFlag, SpriteConst } from "./SpriteConst";
import { AlphaCmd } from "./cmd/AlphaCmd";
import { ClipRectCmd } from "./cmd/ClipRectCmd";
import { Draw9GridTextureCmd } from "./cmd/Draw9GridTextureCmd";
import { DrawCircleCmd } from "./cmd/DrawCircleCmd";
import { DrawCurvesCmd } from "./cmd/DrawCurvesCmd";
import { DrawImageCmd } from "./cmd/DrawImageCmd";
import { DrawLineCmd } from "./cmd/DrawLineCmd";
import { DrawLinesCmd } from "./cmd/DrawLinesCmd";
import { DrawPathCmd } from "./cmd/DrawPathCmd";
import { DrawPieCmd } from "./cmd/DrawPieCmd";
import { DrawPolyCmd } from "./cmd/DrawPolyCmd";
import { DrawRectCmd } from "./cmd/DrawRectCmd";
import { DrawTextureCmd } from "./cmd/DrawTextureCmd";
import { DrawTexturesCmd } from "./cmd/DrawTexturesCmd";
import { DrawTrianglesCmd } from "./cmd/DrawTrianglesCmd";
import { FillTextCmd } from "./cmd/FillTextCmd";
import { FillTextureCmd } from "./cmd/FillTextureCmd";
import { RestoreCmd } from "./cmd/RestoreCmd";
import { RotateCmd } from "./cmd/RotateCmd";
import { SaveCmd } from "./cmd/SaveCmd";
import { ScaleCmd } from "./cmd/ScaleCmd";
import { TransformCmd } from "./cmd/TransformCmd";
import { TranslateCmd } from "./cmd/TranslateCmd";
import { Point } from "../maths/Point";
import { Utils } from "../utils/Utils";
import { ILaya } from "../../ILaya";
import { ColorUtils } from "../utils/ColorUtils";
import { DrawEllipseCmd } from "./cmd/DrawEllipseCmd";
import { DrawRoundRectCmd } from "./cmd/DrawRoundRectCmd";
import { LayaGL } from "../layagl/LayaGL";
export class Graphics {
    static add2DGlobalUniformData(propertyID, propertyKey, uniformtype) {
        let sceneUniformMap = LayaGL.renderDeviceFactory.createGlobalUniformMap("Sprite2DGlobal");
        sceneUniformMap.addShaderUniform(propertyID, propertyKey, uniformtype);
    }
    get useSpriteState() {
        return this._useSpriteState;
    }
    set useSpriteState(value) {
        if (this._useSpriteState == value)
            return;
        this._useSpriteState = value;
        this.repaint();
    }
    constructor() {
        this.owner = null;
        this._useSpriteState = true;
        this._useSpriteRect = false;
        this._cmds = [];
        this._graphicBounds = null;
        this._modified = false;
        this._display = false;
        this._renderDataHandle = LayaGL.render2DRenderPassFactory.create2D2DPrimitiveDataHandle();
    }
    destroy() {
        if (this.owner && this.owner._graphics === this)
            this.owner.setGraphics(null, false);
        for (let cmd of this._cmds) {
            if (!cmd.lock)
                cmd.recover();
        }
        this._cmds.length = 0;
        if (this._material) {
            this._material._removeReference();
            this._material = null;
        }
        this._graphicBounds && this._graphicBounds.destroy();
        this._graphicBounds = null;
        this._renderDataHandle && this._renderDataHandle.destroy();
        this._data = null;
        this.owner = null;
    }
    clear(recoverCmds, exclude) {
        if (this._cmds.length === 0)
            return;
        if (recoverCmds || recoverCmds == null) {
            for (let cmd of this._cmds) {
                if (!cmd.lock && cmd != exclude)
                    cmd.recover();
            }
        }
        if (exclude) {
            this._cmds[0] = exclude;
            this._cmds.length = 1;
        }
        else
            this._cmds.length = 0;
        this._checkDisplay();
        this.repaint();
    }
    _repaint() {
        this.repaint();
    }
    repaint() {
        var _a, _b;
        this._modified = true;
        (_a = this._graphicBounds) === null || _a === void 0 ? void 0 : _a.reset();
        (_b = this.owner) === null || _b === void 0 ? void 0 : _b.repaint(RepaintFlag.Graphics);
    }
    get cmds() {
        return this._cmds;
    }
    set cmds(value) {
        if (this._cmds.length > 0) {
            this._cmds.filter(cmd => !value.includes(cmd)).forEach(cmd => {
                if (!cmd.lock)
                    cmd.recover();
            });
        }
        this._cmds = value;
        this._checkDisplay();
        this.repaint();
    }
    addCmd(cmd, index) {
        if (cmd == null)
            throw new Error("null cmd");
        if (index == null || index >= this._cmds.length)
            this._cmds.push(cmd);
        else
            this._cmds.splice(index, 0, cmd);
        this._checkDisplay();
        this.repaint();
        return cmd;
    }
    removeCmd(cmd, recover) {
        let i = this.cmds.indexOf(cmd);
        if (i != -1) {
            this._cmds.splice(i, 1);
            this._checkDisplay();
            this.repaint();
        }
        if (recover) {
            cmd.lock = false;
            cmd.recover();
        }
    }
    replaceCmd(oldCmd, newCmd, recover) {
        let index = this._cmds.indexOf(oldCmd);
        if (newCmd != null) {
            if (index !== -1)
                this._cmds[index] = newCmd;
            else
                this._cmds.push(newCmd);
            this._checkDisplay();
            this.repaint();
        }
        else if (index != -1) {
            this._cmds.splice(index, 1);
            this._checkDisplay();
            this.repaint();
        }
        if (oldCmd && recover) {
            oldCmd.lock = false;
            oldCmd.recover();
        }
        return newCmd;
    }
    _checkDisplay() {
        if (!this.owner || this.owner.destroyed) {
            this._display = false;
            return;
        }
        let value = !this.owner._renderNode && (this._cmds.length > 0 || this.owner._texture != null);
        if (this._display === value)
            return;
        this._display = value;
        let struct = this.owner._struct;
        if (value) {
            this._modified = true;
            this.owner._initShaderData();
            this.owner._renderType |= SpriteConst.GRAPHICS;
            struct.renderType = BaseRender2DType.graphics;
            struct.renderDataHandler = this._renderDataHandle;
            struct.renderMatrix = this.owner.globalTrans.getMatrix();
            struct.renderElements = this._data._renderElements;
        }
        else {
            this.owner._renderType &= ~SpriteConst.GRAPHICS;
            if (struct.renderElements === this._data._renderElements) {
                struct.renderElements = [];
            }
            struct.renderType = -1;
            struct.renderDataHandler = null;
        }
    }
    getBounds() {
        if (!this._graphicBounds)
            this._graphicBounds = GraphicsBounds.create();
        return this._graphicBounds.getBounds(this);
    }
    getBoundPoints() {
        if (!this._graphicBounds)
            this._graphicBounds = GraphicsBounds.create();
        return this._graphicBounds.getBoundPoints(this);
    }
    get material() {
        return this._material;
    }
    set material(value) {
        if (this._material == value)
            return;
        this._material && this._material._removeReference();
        this._material = value;
        this.repaint();
        if (value != null)
            value._addReference();
    }
    drawImage(texture, x = 0, y = 0, width = null, height = null, color = null) {
        if (!texture)
            return null;
        if (!texture.bitmap)
            return null;
        return this.addCmd(DrawImageCmd.create(texture, x, y, width, height, color));
    }
    drawTexture(texture, x = 0, y = 0, width = null, height = null, matrix = null, alpha = 1, color = null, blendMode = null, uv) {
        if (!texture || alpha < 0.01)
            return null;
        if (!texture.bitmap)
            return null;
        return this.addCmd(DrawTextureCmd.create(texture, x, y, width, height, matrix, alpha, color, blendMode, uv));
    }
    drawTextures(texture, pos, colors) {
        if (!texture)
            return null;
        return this.addCmd(DrawTexturesCmd.create(texture, pos, colors));
    }
    drawTriangles(texture, x, y, vertices, uvs, indices, matrix = null, alpha = 1, color = null, blendMode = null) {
        return this.addCmd(DrawTrianglesCmd.create(texture, x, y, vertices, uvs, indices, matrix, alpha, color, blendMode));
    }
    fillTexture(texture, x, y, width = 0, height = 0, type = "repeat", offset = null, color = null, percent = false) {
        if (texture && texture.bitmap)
            return this.addCmd(FillTextureCmd.create(texture, x, y, width, height, type, offset || Point.EMPTY, color, percent));
        else
            return null;
    }
    clipRect(x, y, width, height) {
        return this.addCmd(ClipRectCmd.create(x, y, width, height));
    }
    fillText(text, x, y, font, color, textAlign) {
        return this.addCmd(FillTextCmd.create(text, x, y, font, color, textAlign, 0, ""));
    }
    fillBorderText(text, x, y, font, fillColor, textAlign, lineWidth, borderColor) {
        return this.addCmd(FillTextCmd.create(text, x, y, font, fillColor, textAlign, lineWidth, borderColor));
    }
    strokeText(text, x, y, font, color, lineWidth, textAlign) {
        return this.addCmd(FillTextCmd.create(text, x, y, font, null, textAlign, lineWidth, color));
    }
    alpha(alpha) {
        return this.addCmd(AlphaCmd.create(alpha));
    }
    transform(matrix, pivotX = 0, pivotY = 0) {
        return this.addCmd(TransformCmd.create(matrix, pivotX, pivotY));
    }
    rotate(angle, pivotX = 0, pivotY = 0) {
        return this.addCmd(RotateCmd.create(angle, pivotX, pivotY));
    }
    scale(scaleX, scaleY, pivotX = 0, pivotY = 0) {
        return this.addCmd(ScaleCmd.create(scaleX, scaleY, pivotX, pivotY));
    }
    translate(tx, ty) {
        return this.addCmd(TranslateCmd.create(tx, ty));
    }
    save() {
        return this.addCmd(SaveCmd.create());
    }
    restore() {
        return this.addCmd(RestoreCmd.create());
    }
    replaceTextColor(color) {
        this.repaint();
        let cmds = this._cmds;
        for (let i = cmds.length - 1; i > -1; i--) {
            let cmd = cmds[i];
            var cmdID = cmd.cmdID;
            switch (cmdID) {
                case FillTextCmd.ID:
                    cmd.color = color;
                    break;
                case DrawImageCmd.ID:
                    cmd.color = color != null ? ColorUtils.create(color).numColor : 0xffffffff;
                    break;
            }
        }
    }
    loadImage(url, x = 0, y = 0, width = null, height = null, complete = null) {
        let tex = ILaya.loader.getRes(url);
        if (tex) {
            this.drawImage(tex, x, y, width, height);
            complete && complete.call(this.owner);
        }
        else {
            ILaya.loader.load(url).then((tex) => {
                this.drawImage(tex, x, y, width, height);
                complete && complete.call(this.owner);
            });
        }
    }
    _render(runner, x = 0, y = 0) {
        if (!this.owner || this.owner.destroyed || this.owner._struct.renderType !== BaseRender2DType.graphics)
            return;
        if (!this._modified
            && this._check()) {
            this._data.setRenderElement(this.owner._struct, this._renderDataHandle);
            return;
        }
        this._data.clear();
        runner.clear();
        runner.sprite = this.owner;
        runner._graphicsData = this._data;
        runner._material = this._material;
        let oldBlendMode = runner.globalCompositeOperation;
        runner.globalCompositeOperation = this.owner._struct.blendMode;
        var cmds = this._cmds;
        for (let i = 0, n = cmds.length; i < n; i++) {
            cmds[i].run(runner, x, y);
        }
        this._renderSpriteTexture(runner, x, y);
        this._data.updateRenderElement(this, this.owner._struct, this._renderDataHandle);
        runner.globalCompositeOperation = oldBlendMode;
        runner._material = null;
        runner._graphicsData = null;
        runner.sprite = null;
        this._modified = false;
    }
    _check() {
        let len = this._data._submits.length;
        for (let i = 0; i < len; i++) {
            let submit = this._data._submits.elements[i];
            let texture = submit._internalInfo.textureHost;
            if (!texture)
                continue;
            let bitmap = texture.bitmap;
            if (bitmap && bitmap.destroyed) {
                return false;
            }
        }
        return true;
    }
    _renderSpriteTexture(runner, x, y) {
        let sprite = this.owner;
        let tex = sprite._texture;
        if (!tex)
            return;
        if (tex._getSource(() => {
            this.owner.graphics.repaint();
        })) {
            var width = sprite._isWidthSet ? sprite._width : tex.sourceWidth;
            var height = sprite._isHeightSet ? sprite._height : tex.sourceHeight;
            var wRate = width / tex.sourceWidth;
            var hRate = height / tex.sourceHeight;
            width = tex.width * wRate;
            height = tex.height * hRate;
            if (width > 0 && height > 0) {
                let px = x + tex.offsetX * wRate;
                let py = y + tex.offsetY * hRate;
                runner.drawTexture(tex, px, py, width, height, 0xffffffff);
            }
        }
    }
    drawLine(fromX, fromY, toX, toY, lineColor, lineWidth = 1) {
        return this.addCmd(DrawLineCmd.create(fromX, fromY, toX, toY, lineColor, lineWidth));
    }
    drawLines(x, y, points, lineColor, lineWidth = 1) {
        if (!points || points.length < 4)
            return null;
        return this.addCmd(DrawLinesCmd.create(x, y, points, lineColor, lineWidth));
    }
    drawCurves(x, y, points, lineColor, lineWidth = 1) {
        return this.addCmd(DrawCurvesCmd.create(x, y, points, lineColor, lineWidth));
    }
    drawRect(x, y, width, height, fillColor, lineColor = null, lineWidth = 1, percent) {
        return this.addCmd(DrawRectCmd.create(x, y, width, height, fillColor, lineColor, lineWidth, percent));
    }
    drawRoundRect(x, y, width, height, lt, rt, lb, rb, fillColor, lineColor = null, lineWidth = 1, percent) {
        return this.addCmd(DrawRoundRectCmd.create(x, y, width, height, lt, rt, lb, rb, fillColor, lineColor, lineWidth, percent));
    }
    drawCircle(x, y, radius, fillColor, lineColor = null, lineWidth = 1, percent) {
        return this.addCmd(DrawCircleCmd.create(x, y, radius, fillColor, lineColor, lineWidth, percent));
    }
    drawEllipse(x, y, width, height, fillColor, lineColor, lineWidth, percent) {
        return this.addCmd(DrawEllipseCmd.create(x, y, width, height, fillColor, lineColor, lineWidth, percent));
    }
    drawPie(x, y, radius, startAngle, endAngle, fillColor, lineColor = null, lineWidth = 1) {
        return this.addCmd(DrawPieCmd.create(x, y, radius, Utils.toRadian(startAngle), Utils.toRadian(endAngle), fillColor, lineColor, lineWidth));
    }
    drawPoly(x, y, points, fillColor, lineColor = null, lineWidth = 1) {
        return this.addCmd(DrawPolyCmd.create(x, y, points, fillColor, lineColor, lineWidth));
    }
    drawPath(x, y, paths, brush = null, pen = null) {
        return this.addCmd(DrawPathCmd.create(x, y, paths, brush, pen));
    }
    draw9Grid(texture, x = 0, y = 0, width = 0, height = 0, sizeGrid, color) {
        this.addCmd(Draw9GridTextureCmd.create(texture, x, y, width, height, sizeGrid, false, color));
    }
}
